import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { REPORTS } from '../../../../../js/constants';
import { GROUP_OPTIONS } from '../modals/chartCreationConstants';
import { priorityMapping } from '../utils';
import BarGraph from './Bar/BarGraph';
import BurndownGraph from './Burndown/BurndownGraph';
import BurnupGraph from './Burnup/BurnupGraph';
import { CHART_TYPE_DEFINITIONS, CHART_TYPES, DRILLDOWN_KEY } from './constants';
import GroupedBarGraph from './GroupedBar/GroupedBarGraph';
import LineGraph from './Line/LineGraph';
import NumberChart from './Number/NumberChart';
import PieGraph from './Pie/PieGraph';
import StackedBarGraph from './StackedBar/StackedBarGraph';

const ChartComponent = ({ chartType, ...props }) => {
  const Chart = useMemo(() => {
    switch (chartType) {
      case CHART_TYPE_DEFINITIONS[CHART_TYPES.BAR].key: {
        return BarGraph;
      }
      case CHART_TYPE_DEFINITIONS[CHART_TYPES.GROUPED_BAR].key: {
        return GroupedBarGraph;
      }
      case CHART_TYPE_DEFINITIONS[CHART_TYPES.STACKED_BAR].key: {
        return StackedBarGraph;
      }
      case CHART_TYPE_DEFINITIONS[CHART_TYPES.LINE].key: {
        return LineGraph;
      }
      case CHART_TYPE_DEFINITIONS[CHART_TYPES.BURNUP].key: {
        return BurnupGraph;
      }
      case CHART_TYPE_DEFINITIONS[CHART_TYPES.BURNDOWN].key: {
        return BurndownGraph;
      }
      case CHART_TYPE_DEFINITIONS[CHART_TYPES.NUMBER].key: {
        return NumberChart;
      }
      case CHART_TYPE_DEFINITIONS[CHART_TYPES.PIE].key:
      default: {
        return PieGraph;
      }
    }
  }, [chartType]);

  return <Chart {...props} />;
};

// Every prepared row carries `taskIds` (present only while the server still
// embeds them) AND, under DRILLDOWN_KEY, the raw group/stack/date keys the
// aggregation actually emitted. The raw keys exist in both response shapes, so
// a click can always be resolved: from the embedded ids when they are there,
// via the drill-down endpoint when they are not.
const prepareChartData = (data) => {
  // stacked and grouped bar charts
  if (data?.stackBy) {
    const groupedData = {};

    data?.aggregated?.forEach((item) => {
      // Drop novalue groups and stacks for custom fields
      if (
        (data.stackBy?.includes('customFields') && item?.stack === 'novalue') ||
        (data.groupBy?.includes('customFields') && item?.group === 'novalue')
      ) {
        return;
      }

      let groupKey;

      switch (data?.groupBy) {
        case GROUP_OPTIONS.ASSIGNEE: {
          groupKey = item?.group || 'Unassigned';
          break;
        }
        case GROUP_OPTIONS.TAG: {
          groupKey = item?.group || 'None';
          break;
        }
        case GROUP_OPTIONS.PRIORITY: {
          groupKey = priorityMapping[item?.group] || '--';
          break;
        }
        case GROUP_OPTIONS.COMPLETION: {
          groupKey =
            item?.group === 'completed' ? 'Completed' : 'Not completed';
          break;
        }
        default: {
          groupKey = item?.group || '--';
        }
      }

      let stackKey;

      switch (data?.stackBy) {
        case GROUP_OPTIONS.ASSIGNEE: {
          stackKey = item?.stack || 'Unassigned';
          break;
        }
        case GROUP_OPTIONS.TAG: {
          stackKey = item?.stack || 'None';
          break;
        }
        case GROUP_OPTIONS.PRIORITY: {
          stackKey = priorityMapping[item?.stack] || '--';
          break;
        }
        case GROUP_OPTIONS.COMPLETION: {
          stackKey =
            item?.stack === 'completed' ? 'Completed' : 'Not completed';
          break;
        }
        default: {
          stackKey = item?.stack || '--';
        }
      }

      if (!groupedData[groupKey]) {
        groupedData[groupKey] = {
          name: groupKey,
          group: groupKey,
          taskIds: item?.taskIds,
          // `group`/`name` above are display labels; `stacks` maps the display
          // series key recharts reports on click back to the raw stack value.
          // Merging several aggregated rows into one chart row is why the
          // pre-existing `taskIds` above only ever holds the FIRST stack's ids —
          // the drill-down path resolves (raw group, raw stack) server-side
          // instead, so that quirk cannot affect it.
          [DRILLDOWN_KEY]: { group: item?.group, stacks: {} },
        };
      }

      groupedData[groupKey][DRILLDOWN_KEY].stacks[stackKey] = item?.stack;

      if (item?.count > 0) {
        groupedData[groupKey][stackKey] = item.count;
      }
    });

    return Object.values(groupedData);
  }

  if (data.groupBy?.includes('customFields')) {
    return data?.aggregated
      ?.filter((v) => v?.group !== 'novalue')
      ?.map((v) => ({
        name: v?.group,
        value: v?.count,
        taskIds: v?.taskIds,
        [DRILLDOWN_KEY]: { group: v?.group },
      }));
  }

  // regular charts
  switch (data?.groupBy) {
    case GROUP_OPTIONS.COMPLETION: {
      const COMPLETION_ORDER = ['incomplete', 'completed'];
      return data?.aggregated
        ?.slice()
        ?.sort(
          (a, b) =>
            COMPLETION_ORDER.indexOf(a?.group) -
            COMPLETION_ORDER.indexOf(b?.group),
        )
        ?.map((v) => ({
          name: v?.group === 'completed' ? 'Completed' : 'Not completed',
          value: v?.count,
          taskIds: v?.taskIds,
          [DRILLDOWN_KEY]: { group: v?.group },
        }));
    }
    case GROUP_OPTIONS.CREATOR:
    case GROUP_OPTIONS.ASSIGNEE: {
      return data?.aggregated?.map((v) => ({
        name: v?.group === null ? 'Unassigned' : v?.group,
        value: v?.count,
        taskIds: v?.taskIds,
        [DRILLDOWN_KEY]: { group: v?.group },
      }));
    }
    case GROUP_OPTIONS.PRIORITY: {
      const PRIORITY_ORDER = [
        'nopriority',
        'low',
        'medium',
        'high',
        'critical',
      ];
      return data?.aggregated
        ?.slice()
        ?.sort(
          (a, b) =>
            PRIORITY_ORDER.indexOf(a?.group) - PRIORITY_ORDER.indexOf(b?.group),
        )
        ?.map((v) => ({
          name: priorityMapping[v?.group] || '--',
          value: v?.count,
          taskIds: v?.taskIds,
          [DRILLDOWN_KEY]: { group: v?.group },
        }));
    }
    case GROUP_OPTIONS.PROJECT: {
      return data?.aggregated?.map((v) => ({
        name: v?.group,
        value: v?.count,
        taskIds: v?.taskIds,
        [DRILLDOWN_KEY]: { group: v?.group },
      }));
    }
    case GROUP_OPTIONS.TAG: {
      return data?.aggregated?.map((v) => ({
        name: v?.group === null ? 'None' : v?.group,
        value: v?.count,
        taskIds: v?.taskIds,
        [DRILLDOWN_KEY]: { group: v?.group },
      }));
    }
    case GROUP_OPTIONS.BURN_REPORT: {
      return data?.aggregated?.map((item) => ({
        name: item?.date,
        date: item?.date,
        total: item?.total || 0,
        completed: item?.completed || 0,
        remaining: item?.remaining || 0,
        // Kept as `|| []` so nothing downstream sees `undefined` where it used
        // to see an array; the drill-down path keys off the empty length, not
        // off the field being absent.
        taskIds: item?.taskIds || [],
        [DRILLDOWN_KEY]: { date: item?.date },
      }));
    }
    default: {
      return [];
    }
  }
};

const ChartWrapper = ({ chartType, data, isLoading, measure, ...chartProps }) => {
  const intl = useIntl();

  const yAxisLabel = useMemo(() => {
    switch (measure) {
      case REPORTS.MEASURES.TASK_COUNT:
      default: {
        return intl.formatMessage({
          id: 'TASKS_COUNT_LABEL',
          defaultMessage: 'Tasks (count in numbers)',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure]);

  const chartData = useMemo(() => prepareChartData(data), [data]);

  if (!chartType) {
    return null;
  }

  return (
    <ChartComponent
      chartType={chartType}
      chartData={chartData}
      isLoading={isLoading}
      summary={data?.summary}
      // Server-computed de-duplicated task total. Always sent (both response
      // shapes); consumed by PieGraph, which can no longer derive it from
      // per-bucket taskIds once those stop being shipped. Undefined for data
      // cached by an older build — consumers fall back to the old computation.
      uniqueTaskCount={data?.uniqueTaskCount}
      yAxisLabel={yAxisLabel}
      xLabelType={data?.groupBy}
      xLabelEntities={data?.entities?.[data?.groupBy]}
      stackByType={data?.stackBy}
      stackByEntities={data?.entities?.[data?.stackBy]}
      {...chartProps}
    />
  );
};

export default ChartWrapper;
